/**
 * ZoneDetails.jsx — iter847 (Feb 2026)
 * Extracted from BreastHealthIntelligencePage.jsx. Renders the details
 * card for a selected anatomical zone (epithelium / stroma / etc.) with
 * band pill, twin attribution chips, and risk factor bullet list.
 */
import React from "react";
import { motion } from "framer-motion";
import TwinAttributionBadge from "@/components/breast_intel/TwinAttributionBadge";

const BAND_PILL = {
  green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  red:    "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
  insufficient_data: "bg-slate-100 text-slate-500 dark:bg-slate-500/20 dark:text-slate-300",
};

export default function ZoneDetails({ zone, biology, t, cardCls, lang, twinAttribution }) {
  const z = biology?.[zone];
  if (!z) return null;
  const labels = {
    epithelium: lang === "bg" ? "Епителна среда" : lang === "es" ? "Entorno epitelial" : "Epithelial environment",
    stroma:     lang === "bg" ? "Стромална среда" : lang === "es" ? "Entorno estromal" : "Stromal environment",
    adipose:    lang === "bg" ? "Мастна среда" : lang === "es" ? "Entorno adiposo" : "Adipose environment",
    immune:     lang === "bg" ? "Имунна среда" : lang === "es" ? "Entorno inmune" : "Immune environment",
    vascular:   lang === "bg" ? "Съдова среда" : lang === "es" ? "Entorno vascular" : "Vascular environment",
    skin:       lang === "bg" ? "Кожа" : lang === "es" ? "Piel" : "Skin",
    lymphatic:  lang === "bg" ? "Лимфна система" : lang === "es" ? "Sistema linfático" : "Lymphatic system",
    ligaments:  lang === "bg" ? "Куперови връзки" : lang === "es" ? "Ligamentos de Cooper" : "Cooper's ligaments",
  };
  const zoneAttribution = twinAttribution?.biology?.[zone];
  const twinRegistry = twinAttribution?.twin_registry || {};
  return (
    <motion.div
      key={zone}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${cardCls} p-5`}
      data-testid={`zone-details-${zone}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold">{labels[zone]}</h3>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${BAND_PILL[z.band]}`} data-testid={`zone-band-${zone}`}>
          {t.statusBands[z.band]} · {z.score}/100
        </span>
      </div>
      {zoneAttribution?.twins?.length > 0 && (
        <div className="mb-3" data-testid={`zone-twin-attr-${zone}`}>
          <TwinAttributionBadge twins={zoneAttribution.twins} registry={twinRegistry} markers={zoneAttribution.markers} lang={lang} size="xs" />
          {zoneAttribution.markers?.length > 0 && (
            <div className="mt-1.5 text-[10px] opacity-60">
              {lang === "bg" ? "Маркери: " : lang === "es" ? "Marcadores: " : "Markers: "}
              {zoneAttribution.markers.join(" · ")}
            </div>
          )}
        </div>
      )}
      <div className="text-xs opacity-70 mb-2">{t.factors}:</div>
      <ul className="space-y-1.5 text-sm">
        {z.factors && z.factors.length ? z.factors.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-pink-500 mt-1">•</span>
            <span>{f}</span>
          </li>
        )) : (
          <li className="opacity-50 italic text-xs">
            {lang === "en" ? "No specific risk factors detected at the moment." : lang === "es" ? "Sin factores específicos detectados." : "Без специфични фактори към момента."}
          </li>
        )}
      </ul>
    </motion.div>
  );
}
